import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Send, ListChecks, FileText, ClipboardPaste,
  AlertTriangle, CheckCircle2, Loader2, Search, ChevronRight,
} from 'lucide-react';

interface Template { id: string; name: string; current_published_version?: number }
interface Site { id: string; name: string; site_reference?: string; client_id?: string; broker_id?: string; physical_address?: string; city?: string; default_survey_template_id?: string | null }
interface Client { id: string; name: string }

type PickerMode = 'pick' | 'paste' | 'csv';

interface CsvRow {
  site_reference: string;
  due_date?: string;
  notes?: string;
  surveyor_email?: string;
}

interface BulkResponse {
  batch_id: string;
  template_id: string;
  template_version: number;
  template_name: string;
  created: {
    instruction_id: string;
    site_id: string;
    site_reference?: string;
    client_name: string;
    recipient_name?: string | null;
    recipient_phone?: string | null;
    public_link: string;
    token: string;
  }[];
  failed: {
    index: number;
    site_id?: string;
    site_reference?: string;
    reason: string;
  }[];
  summary: { requested: number; created: number; failed: number };
}

const parseCsv = (raw: string): CsvRow[] => {
  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return [];
  // Detect optional header
  const first = lines[0].toLowerCase();
  const hasHeader = first.includes('site_reference') || first.includes('site reference') || first.startsWith('ref');
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const headerCells = hasHeader ? lines[0].split(',').map((c) => c.trim().toLowerCase()) : ['site_reference', 'due_date', 'notes', 'surveyor_email'];
  const idxOf = (name: string) => headerCells.indexOf(name);
  const refIdx = Math.max(0, idxOf('site_reference'));
  const dueIdx = idxOf('due_date');
  const notesIdx = idxOf('notes');
  const emailIdx = idxOf('surveyor_email');
  return dataLines.map((line) => {
    const cells = line.split(',').map((c) => c.trim());
    return {
      site_reference: cells[refIdx] || '',
      due_date: dueIdx >= 0 ? cells[dueIdx] : undefined,
      notes: notesIdx >= 0 ? cells[notesIdx] : undefined,
      surveyor_email: emailIdx >= 0 ? cells[emailIdx] : undefined,
    };
  }).filter((r) => r.site_reference);
};

export const BulkDispatchPage: React.FC = () => {
  const nav = useNavigate();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  const [templateId, setTemplateId] = useState('');
  const [siteTplOverride, setSiteTplOverride] = useState<Record<string, string>>({});
  const [method, setMethod] = useState<'on_site' | 'remote_self'>('on_site');
  const [defaultDueDate, setDefaultDueDate] = useState('');
  const [defaultNotes, setDefaultNotes] = useState('');
  const [defaultSurveyorEmail, setDefaultSurveyorEmail] = useState('');

  const [mode, setMode] = useState<PickerMode>('pick');
  const [siteSearch, setSiteSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<string>>(new Set());
  const [collapsedClients, setCollapsedClients] = useState<Set<string>>(new Set());
  const [pastedRefs, setPastedRefs] = useState('');
  const [csvRows, setCsvRows] = useState<CsvRow[]>([]);
  const [csvFileName, setCsvFileName] = useState<string>('');

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkResponse | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [tpl, s, c] = await Promise.all([
          api.get<Template[]>('/templates'),
          api.get<Site[]>('/sites?limit=10000&fields=picker'),
          api.get<Client[]>('/clients'),
        ]);
        // Filter to published templates only
        const publishedTemplates = tpl.data.filter((t) => t.current_published_version);
        setTemplates(publishedTemplates);
        setSites(s.data);
        setClients(c.data);
        if (publishedTemplates.length) setTemplateId(publishedTemplates[0].id);
      } catch (err) {
        toast.error('Failed to load templates or sites.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredSites = useMemo(() => {
    let out = sites;
    if (clientFilter) out = out.filter((s) => s.client_id === clientFilter);
    if (siteSearch.trim()) {
      const q = siteSearch.trim().toLowerCase();
      out = out.filter((s) =>
        s.name?.toLowerCase().includes(q)
        || s.site_reference?.toLowerCase().includes(q)
        || s.physical_address?.toLowerCase().includes(q)
        || s.city?.toLowerCase().includes(q),
      );
    }
    return out.slice(0, 500); // safety cap for rendering
  }, [sites, siteSearch, clientFilter]);

  const toggleSite = (id: string) => {
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllFiltered = () => {
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      filteredSites.forEach((s) => next.add(s.id));
      return next;
    });
  };
  const clearSelection = () => setSelectedSiteIds(new Set());

  // Group filtered sites by client for a readable "pick" experience.
  interface SiteGroup { client_id: string; client_name: string; sites: Site[]; selected_count: number }
  const groupedSites: SiteGroup[] = useMemo(() => {
    const nameById = new Map(clients.map((c) => [c.id, c.name]));
    const byClient = new Map<string, Site[]>();
    for (const s of filteredSites) {
      const cid = s.client_id || '__none__';
      const arr = byClient.get(cid) || [];
      arr.push(s);
      byClient.set(cid, arr);
    }
    return Array.from(byClient.entries())
      .map(([cid, arr]): SiteGroup => ({
        client_id: cid,
        client_name: cid === '__none__' ? '(No client)' : (nameById.get(cid) || cid),
        sites: arr,
        selected_count: arr.filter((s) => selectedSiteIds.has(s.id)).length,
      }))
      .sort((a, b) => a.client_name.localeCompare(b.client_name));
  }, [filteredSites, clients, selectedSiteIds]);

  const toggleGroupCollapsed = (cid: string) => {
    setCollapsedClients((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };
  const selectAllForClient = (cid: string) => {
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      const grp = groupedSites.find((g) => g.client_id === cid);
      grp?.sites.forEach((s) => next.add(s.id));
      return next;
    });
  };
  const clearForClient = (cid: string) => {
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      const grp = groupedSites.find((g) => g.client_id === cid);
      grp?.sites.forEach((s) => next.delete(s.id));
      return next;
    });
  };

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseCsv(String(reader.result || ''));
      setCsvRows(rows);
      if (rows.length === 0) toast.error('CSV parsed but no rows found. Expected column: site_reference (required), plus optional due_date, notes, surveyor_email.');
      else toast.success(`${rows.length} row(s) parsed from CSV.`);
    };
    reader.onerror = () => toast.error('Could not read file.');
    reader.readAsText(file);
  };

  const pastedRefsList = useMemo(() =>
    pastedRefs.split(/\r?\n/).map((r) => r.trim()).filter(Boolean),
  [pastedRefs]);

  // Build the payload preview
  const siteById = useMemo(() => new Map(sites.map((s) => [s.id, s])), [sites]);
  const resolvedTplFor = (sid: string): string => siteTplOverride[sid] ?? siteById.get(sid)?.default_survey_template_id ?? templateId ?? '';
  const tplName = (tid: string): string => templates.find((t) => t.id === tid)?.name || '';
  const setTplOverride = (sid: string, tid: string): void => setSiteTplOverride((p) => ({ ...p, [sid]: tid }));
  const selectedSitesArr = useMemo(
    () => Array.from(selectedSiteIds).map((id) => siteById.get(id)).filter(Boolean) as Site[],
    [selectedSiteIds, siteById],
  );
  const allPickHaveTpl = mode !== 'pick' || selectedSitesArr.every((s) => resolvedTplFor(s.id));

  const buildItems = () => {
    if (mode === 'pick') {
      return Array.from(selectedSiteIds).map((sid) => ({ site_id: sid, template_id: resolvedTplFor(sid) || undefined }));
    }
    if (mode === 'paste') {
      return pastedRefsList.map((ref) => ({ site_reference: ref }));
    }
    return csvRows.map((r) => ({
      site_reference: r.site_reference,
      due_date: r.due_date || undefined,
      notes: r.notes || undefined,
      surveyor_email: r.surveyor_email || undefined,
    }));
  };

  const previewItems = buildItems();
  const canSubmit = previewItems.length > 0 && !submitting && allPickHaveTpl && (mode === 'pick' || !!templateId);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setResult(null);
    try {
      const payload = {
        template_id: templateId,
        method,
        default_due_date: defaultDueDate || undefined,
        default_notes: defaultNotes || undefined,
        default_surveyor_email: defaultSurveyorEmail || undefined,
        items: previewItems,
      };
      const r = await api.post<BulkResponse>('/instructions/bulk', payload);
      setResult(r.data);
      if (r.data.summary.created > 0) toast.success(`Dispatched ${r.data.summary.created} instruction(s).`);
      if (r.data.summary.failed > 0) toast.warning(`${r.data.summary.failed} row(s) failed. See report below.`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Bulk dispatch failed.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForNextBatch = () => {
    setResult(null);
    setSelectedSiteIds(new Set());
    setPastedRefs('');
    setCsvRows([]);
    setCsvFileName('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-raptor-navy/50" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bulk-dispatch-page">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => nav('/insurer/instructions')}
            className="inline-flex items-center gap-1 text-xs text-raptor-navy/60 hover:text-raptor-navy mb-2 font-body"
            data-testid="bulk-back-btn"
          >
            <ArrowLeft size={14} /> Back to Instructions
          </button>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Bulk Inspection Dispatch</h2>
          <p className="text-sm text-raptor-navy/60 font-body">
            Issue the same survey to many sites in one go. Pick sites, paste references, or upload a CSV.
          </p>
        </div>
      </div>

      {/* Step 1 — Template + defaults */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Template &amp; defaults</CardTitle>
          <CardDescription>Each site uses its own allocated template where set. This default applies to sites without one (and to pasted / CSV references). Per-site overrides win.</CardDescription>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 font-body flex items-center gap-2">
              <AlertTriangle size={16} /> No published templates yet. Publish a template first from the Survey Templates page.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Default template</Label>
                <Select
                  data-testid="bulk-template"
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                >
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.current_published_version ? ` (v${t.current_published_version})` : ''}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <Select
                  data-testid="bulk-method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as 'on_site' | 'remote_self')}
                >
                  <option value="on_site">On-site (surveyor visits)</option>
                  <option value="remote_self">Remote self-submission</option>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Default due date <span className="text-raptor-navy/40 text-xs font-normal">(optional)</span></Label>
                <Input
                  data-testid="bulk-default-due"
                  type="date"
                  value={defaultDueDate}
                  onChange={(e) => setDefaultDueDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Default surveyor email <span className="text-raptor-navy/40 text-xs font-normal">(optional)</span></Label>
                <Input
                  data-testid="bulk-default-email"
                  type="email"
                  placeholder="surveyor@example.co.za"
                  value={defaultSurveyorEmail}
                  onChange={(e) => setDefaultSurveyorEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Default notes (internal) <span className="text-raptor-navy/40 text-xs font-normal">(optional)</span></Label>
                <Textarea
                  data-testid="bulk-default-notes"
                  rows={2}
                  placeholder="Notes stamped onto every instruction in this batch unless overridden per row."
                  value={defaultNotes}
                  onChange={(e) => setDefaultNotes(e.target.value)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2 — Site selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">2. Choose sites</CardTitle>
          <CardDescription>Pick from the list, paste references, or upload a CSV.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 flex-wrap" role="tablist">
            <Button
              size="sm"
              variant={mode === 'pick' ? 'gradient' : 'outline'}
              onClick={() => setMode('pick')}
              data-testid="bulk-mode-pick"
            >
              <ListChecks size={14} /> Pick from list
            </Button>
            <Button
              size="sm"
              variant={mode === 'paste' ? 'gradient' : 'outline'}
              onClick={() => setMode('paste')}
              data-testid="bulk-mode-paste"
            >
              <ClipboardPaste size={14} /> Paste references
            </Button>
            <Button
              size="sm"
              variant={mode === 'csv' ? 'gradient' : 'outline'}
              onClick={() => setMode('csv')}
              data-testid="bulk-mode-csv"
            >
              <FileText size={14} /> CSV upload
            </Button>
          </div>

          {mode === 'pick' && (
            <div className="space-y-3">
              <div className="grid md:grid-cols-3 gap-3">
                <div className="md:col-span-2 relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-raptor-navy/40" />
                  <Input
                    className="pl-9"
                    data-testid="bulk-site-search"
                    placeholder="Search by name, reference, address, city…"
                    value={siteSearch}
                    onChange={(e) => setSiteSearch(e.target.value)}
                  />
                </div>
                <Select
                  data-testid="bulk-client-filter"
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                >
                  <option value="">All clients</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
              <div className="flex items-center justify-between text-xs font-body text-raptor-navy/60">
                <div>Showing {filteredSites.length} of {sites.length} sites {siteSearch || clientFilter ? '(filtered)' : ''}</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllFiltered}
                    className="hover:underline text-raptor-blue"
                    data-testid="bulk-select-all"
                  >Select all filtered</button>
                  <span className="text-raptor-navy/30">·</span>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="hover:underline text-raptor-navy/60"
                    data-testid="bulk-clear-selection"
                  >Clear ({selectedSiteIds.size})</button>
                </div>
              </div>
              <div className="border border-border/60 rounded-md max-h-[420px] overflow-y-auto divide-y divide-border/40" data-testid="bulk-site-list">
                {groupedSites.length === 0 && (
                  <div className="py-8 text-center text-sm text-raptor-navy/50">No sites match.</div>
                )}
                {groupedSites.map((grp) => {
                  const collapsed = collapsedClients.has(grp.client_id);
                  const allSelected = grp.selected_count === grp.sites.length && grp.sites.length > 0;
                  return (
                    <div key={grp.client_id} data-testid={`bulk-group-${grp.client_id}`}>
                      <div className="flex items-center gap-2 bg-raptor-neutral/40 px-3 py-2 text-xs font-body sticky top-0 z-10">
                        <button
                          type="button"
                          onClick={() => toggleGroupCollapsed(grp.client_id)}
                          className="text-raptor-navy/60 hover:text-raptor-navy"
                          aria-label={collapsed ? 'Expand' : 'Collapse'}
                          data-testid={`bulk-group-toggle-${grp.client_id}`}
                        >
                          <ChevronRight
                            size={14}
                            className={`transition-transform ${collapsed ? '' : 'rotate-90'}`}
                          />
                        </button>
                        <div className="font-semibold text-raptor-navy flex-1 truncate">
                          {grp.client_name}
                          <span className="ml-2 text-raptor-navy/50 font-tabular">
                            {grp.selected_count} / {grp.sites.length} selected
                          </span>
                        </div>
                        {allSelected ? (
                          <button
                            type="button"
                            onClick={() => clearForClient(grp.client_id)}
                            className="text-raptor-navy/60 hover:text-red-700"
                            data-testid={`bulk-group-clear-${grp.client_id}`}
                          >Clear</button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => selectAllForClient(grp.client_id)}
                            className="text-raptor-blue hover:underline"
                            data-testid={`bulk-group-select-all-${grp.client_id}`}
                          >Select all</button>
                        )}
                      </div>
                      {!collapsed && (
                        <table className="w-full text-sm font-body">
                          <tbody>
                            {grp.sites.map((s) => {
                              const checked = selectedSiteIds.has(s.id);
                              return (
                                <tr
                                  key={s.id}
                                  className={`border-t border-border/40 cursor-pointer hover:bg-raptor-neutral/30 ${checked ? 'bg-raptor-blue/5' : ''}`}
                                  onClick={() => toggleSite(s.id)}
                                >
                                  <td className="py-1.5 pl-8 pr-2 w-8">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => toggleSite(s.id)}
                                      onClick={(e) => e.stopPropagation()}
                                      data-testid={`bulk-site-check-${s.id}`}
                                    />
                                  </td>
                                  <td className="py-1.5 px-2 font-semibold">{s.name}</td>
                                  <td className="py-1.5 px-2 font-tabular text-xs text-raptor-navy/70">{s.site_reference || '—'}</td>
                                  <td className="py-1.5 px-2 text-xs text-raptor-navy/70">{s.city || s.physical_address?.slice(0, 40) || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {mode === 'paste' && (
            <div className="space-y-2">
              <Label>Paste site references — one per line</Label>
              <Textarea
                data-testid="bulk-paste-textarea"
                rows={10}
                placeholder={"MEI-00123\nMEI-00124\nMEI-00125\n…"}
                value={pastedRefs}
                onChange={(e) => setPastedRefs(e.target.value)}
                className="font-tabular"
              />
              <p className="text-xs text-raptor-navy/60 font-body">
                {pastedRefsList.length} reference(s) parsed. Each is matched against ``site_reference`` within your tenant.
              </p>
            </div>
          )}

          {mode === 'csv' && (
            <div className="space-y-3">
              <div className="rounded-md border border-border/60 bg-raptor-neutral/20 p-3 text-xs font-body text-raptor-navy/70">
                <div className="font-semibold text-raptor-navy mb-1">CSV format</div>
                <div>
                  Required column: <code className="font-tabular bg-white px-1 rounded">site_reference</code>.
                  Optional columns: <code className="font-tabular bg-white px-1 rounded">due_date</code> (YYYY-MM-DD),
                  <code className="font-tabular bg-white px-1 rounded ml-1">notes</code>,
                  <code className="font-tabular bg-white px-1 rounded ml-1">surveyor_email</code>.
                  A header row is optional — if omitted, we assume the order above.
                </div>
              </div>
              <label className="inline-flex items-center gap-2 rounded-md border border-dashed border-raptor-blue/40 bg-raptor-blue/5 hover:bg-raptor-blue/10 cursor-pointer px-4 py-6 w-full justify-center transition-colors">
                <Upload size={18} className="text-raptor-blue" />
                <span className="text-sm font-body text-raptor-navy">
                  {csvFileName ? `Replace: ${csvFileName}` : 'Click to upload a CSV file'}
                </span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleCsvUpload}
                  data-testid="bulk-csv-input"
                />
              </label>
              {csvRows.length > 0 && (
                <div className="border border-border/60 rounded-md max-h-[280px] overflow-y-auto">
                  <table className="w-full text-sm font-body">
                    <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70 sticky top-0">
                      <tr>
                        <th className="py-2 px-3 text-left">Reference</th>
                        <th className="py-2 px-3 text-left">Due date</th>
                        <th className="py-2 px-3 text-left">Notes</th>
                        <th className="py-2 px-3 text-left">Surveyor email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvRows.slice(0, 200).map((r, i) => (
                        <tr key={i} className="border-t border-border/40">
                          <td className="py-2 px-3 font-tabular text-xs">{r.site_reference}</td>
                          <td className="py-2 px-3 text-xs">{r.due_date || '—'}</td>
                          <td className="py-2 px-3 text-xs truncate max-w-[220px]">{r.notes || '—'}</td>
                          <td className="py-2 px-3 text-xs">{r.surveyor_email || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvRows.length > 200 && (
                    <div className="p-2 text-xs text-raptor-navy/50 font-body text-center border-t border-border/40">
                      +{csvRows.length - 200} more rows will be dispatched.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 3 — Submit */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">3. Review &amp; dispatch</CardTitle>
          {mode === 'pick' && <CardDescription>Confirm the template allocated to each site. Change any before dispatching.</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-4">
          {mode === 'pick' && selectedSitesArr.length > 0 && (
            <div className="border border-border/70 rounded-md overflow-hidden" data-testid="bulk-review-table">
              <table className="w-full text-sm font-body">
                <thead className="bg-raptor-neutral/40 text-[11px] uppercase text-raptor-navy/60">
                  <tr>
                    <th className="py-2 px-3 text-left">Site</th>
                    <th className="py-2 px-3 text-left w-[44%]">Survey template *</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50 max-h-[320px]">
                  {selectedSitesArr.slice(0, 200).map((s) => {
                    const val = resolvedTplFor(s.id);
                    const fromDefault = !siteTplOverride[s.id] && !!s.default_survey_template_id;
                    return (
                      <tr key={s.id} data-testid={`bulk-review-${s.id}`} className={val ? '' : 'bg-amber-50/40'}>
                        <td className="py-2 px-3">
                          <div className="text-raptor-navy font-semibold">{s.name}</div>
                          {s.site_reference && <div className="text-[11px] text-raptor-navy/50 font-tabular">{s.site_reference}</div>}
                        </td>
                        <td className="py-2 px-3">
                          <Select value={val} onChange={(e) => setTplOverride(s.id, e.target.value)} data-testid={`bulk-review-tpl-${s.id}`}>
                            <option value="">— Select template —</option>
                            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </Select>
                          {fromDefault && <div className="text-[11px] text-raptor-navy/50 mt-0.5">Site default</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {selectedSitesArr.length > 200 && (
                <div className="p-2 text-xs text-raptor-navy/50 font-body text-center border-t border-border/40">
                  +{selectedSitesArr.length - 200} more sites will use their allocated or default template.
                </div>
              )}
            </div>
          )}
          {mode === 'pick' && !allPickHaveTpl && (
            <div className="text-[11px] text-amber-600 font-body">Every selected site needs a template before you can dispatch.</div>
          )}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm font-body text-raptor-navy/70">
              <span className="font-semibold text-raptor-navy">{previewItems.length}</span> instruction(s) will be issued
              {mode !== 'pick' && templateId && tplName(templateId) && (
                <> — default template <span className="font-semibold text-raptor-navy">{tplName(templateId)}</span></>
              )}.
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => nav('/insurer/instructions')}
                disabled={submitting}
                data-testid="bulk-cancel-btn"
              >Cancel</Button>
              <Button
                variant="gradient"
                onClick={submit}
                disabled={!canSubmit}
                data-testid="bulk-submit-btn"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Dispatching…</> : <><Send size={16} /> Dispatch batch</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Result panel */}
      {result && (
        <Card data-testid="bulk-result-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-600" /> Batch dispatched
            </CardTitle>
            <CardDescription>
              Batch ID <code className="font-tabular text-xs">{result.batch_id}</code>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3 flex-wrap">
              <Badge variant="muted" data-testid="bulk-result-requested">Requested: {result.summary.requested}</Badge>
              <Badge variant="success" data-testid="bulk-result-created">Created: {result.summary.created}</Badge>
              {result.summary.failed > 0 && (
                <Badge variant="destructive" data-testid="bulk-result-failed">Failed: {result.summary.failed}</Badge>
              )}
            </div>

            {result.failed.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase text-raptor-navy/60 mb-1">Failed rows</div>
                <div className="border border-amber-200 rounded-md bg-amber-50/50 max-h-[220px] overflow-y-auto">
                  <table className="w-full text-sm font-body">
                    <thead className="bg-amber-100/60 text-xs uppercase text-amber-900">
                      <tr>
                        <th className="py-2 px-3 text-left">Row</th>
                        <th className="py-2 px-3 text-left">Site</th>
                        <th className="py-2 px-3 text-left">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.failed.map((f) => (
                        <tr key={f.index} className="border-t border-amber-200/60">
                          <td className="py-2 px-3 font-tabular text-xs">{f.index + 1}</td>
                          <td className="py-2 px-3 text-xs">{f.site_id || f.site_reference || '—'}</td>
                          <td className="py-2 px-3 text-xs text-amber-900">{f.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.created.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase text-raptor-navy/60 mb-1">Created instructions ({result.created.length})</div>
                <div className="border border-border/60 rounded-md max-h-[280px] overflow-y-auto">
                  <table className="w-full text-sm font-body">
                    <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70 sticky top-0">
                      <tr>
                        <th className="py-2 px-3 text-left">Instruction</th>
                        <th className="py-2 px-3 text-left">Client / Site ref</th>
                        <th className="py-2 px-3 text-left">Recipient</th>
                        <th className="py-2 px-3 text-left">Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.created.slice(0, 200).map((c) => (
                        <tr key={c.instruction_id} className="border-t border-border/40">
                          <td className="py-2 px-3 font-tabular text-xs">{c.instruction_id}</td>
                          <td className="py-2 px-3 text-xs">
                            <div className="font-semibold text-raptor-navy">{c.client_name || '—'}</div>
                            {c.site_reference && <div className="text-[11px] text-raptor-navy/60 font-tabular">Ref {c.site_reference}</div>}
                          </td>
                          <td className="py-2 px-3 text-xs">
                            {c.recipient_name ? (
                              <div>
                                <div className="font-semibold text-raptor-navy">{c.recipient_name}</div>
                                {c.recipient_phone && <div className="text-[11px] text-raptor-navy/60 font-tabular">{c.recipient_phone}</div>}
                              </div>
                            ) : (
                              <span className="italic text-raptor-navy/40">
                                {method === 'remote_self' ? 'no whitelist / site contact' : 'on-site (no recipient)'}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-xs">
                            <button
                              onClick={() => { navigator.clipboard.writeText(c.public_link); toast.success('Link copied'); }}
                              className="text-raptor-blue hover:underline font-tabular"
                            >Copy</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {result.created.length > 200 && (
                    <div className="p-2 text-xs text-raptor-navy/50 font-body text-center border-t border-border/40">
                      +{result.created.length - 200} more rows created. See the Instructions page for the full list.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={resetForNextBatch}
                data-testid="bulk-new-batch-btn"
              >Start another batch</Button>
              <Button
                variant="primary"
                onClick={() => nav('/insurer/instructions')}
                data-testid="bulk-view-instructions-btn"
              >View all instructions</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkDispatchPage;
